import { type ReactNode, useEffect } from "react";
import { getRouteApi } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Masthead } from "../components/Masthead";
import { SideRail } from "../components/SideRail";
import { GameStage } from "../components/GameStage";
import { GameScores } from "../components/GameScores";
import { MultiplayerNudge } from "../components/MultiplayerNudge";
import { fetchGame } from "../lib/api";
import { figMeta, isQueuedForPort, runtimeText, waybackUrl } from "../lib/gameMeta";
import type { GameDetail } from "../lib/types";

const route = getRouteApi("/game/$id");

export function GamePage() {
  const { id } = route.useParams();
  const search = route.useSearch();
  const catalog = search.catalog;

  const { data: game, isLoading, isError, error } = useQuery({
    queryKey: ["game", id, catalog ?? "games"],
    queryFn: () => fetchGame(id, catalog),
    retry: false,
  });

  useEffect(() => {
    document.body.classList.add("player-page");
    return () => document.body.classList.remove("player-page");
  }, []);

  useEffect(() => {
    if (game) document.title = `${game.title} — reArcade`;
    return () => {
      document.title = "reArcade";
    };
  }, [game]);

  if (isLoading) {
    // Placeholder rail + stage keep the grid columns and page height identical
    // to the loaded view, so nothing shifts when the game arrives.
    return (
      <>
        <Masthead variant="player" />
        <main className="player-layout">
          <aside className="shelf-rail" aria-hidden="true" />
          <section className="exhibit">
            <header className="exhibit-head">
              <p className="crumb">Reading the catalog…</p>
              <h1>&nbsp;</h1>
            </header>
            <figure className="mount">
              <div className="stage" />
            </figure>
          </section>
        </main>
      </>
    );
  }

  if (isError || !game) {
    const notFound = String((error as Error)?.message || "").startsWith("404");
    return (
      <>
        <Masthead variant="player" />
        <main className="player-layout">
          <aside className="shelf-rail" aria-hidden="true" />
          <section className="exhibit">
            <header className="exhibit-head">
              <p className="crumb">Not on file</p>
              <h1>{id}</h1>
            </header>
            <figure className="mount">
              <div className="stage">
                <div className="stampbox" role="note">
                  <strong>Not on file</strong>
                  <p>
                    {notFound
                      ? "This game is not in the current playable catalog."
                      : "The catalog could not be read."}
                  </p>
                </div>
              </div>
            </figure>
          </section>
        </main>
      </>
    );
  }

  return (
    <PlayerView
      game={game}
      forceLaunch={!!search.forceLaunch}
    />
  );
}

function PlayerView({
  game,
  forceLaunch,
}: {
  game: GameDetail;
  forceLaunch: boolean;
}) {
  const wb = waybackUrl(game);
  const queued = isQueuedForPort(game);

  let stage: ReactNode;
  if (queued && !forceLaunch) stage = <QueuedStamp game={game} />;
  else stage = <GameStage game={game} />;

  return (
    <>
      <Masthead variant="player" />
      <main className="player-layout">
        <SideRail game={game} />
        <section className="exhibit">
          <header className="exhibit-head">
            <p className="crumb">{runtimeText(game)}</p>
            <h1>{game.title}</h1>
          </header>

          <MultiplayerNudge game={game} />

          <figure className="mount">
            {stage}
            <figcaption className="mount-caption">
              <span>{figMeta(game)}</span>
              {wb && (
                <a
                  href={wb.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`Wayback capture, ${wb.year} (opens in new tab)`}
                >
                  Wayback capture, {wb.year} <span aria-hidden="true">↗</span>
                </a>
              )}
            </figcaption>
          </figure>

          <div className="notes">
            <section>
              <h2>Description</h2>
              <p>{game.description || "No archived description was found."}</p>
            </section>
            <section>
              <h2>Instructions</h2>
              <p>{game.instructions || "No archived instructions were found."}</p>
            </section>
            <GameScores gameId={game.id} />
          </div>
        </section>
      </main>
    </>
  );
}

function QueuedStamp({ game }: { game: GameDetail }) {
  return (
    <div className="stage">
      <div className="stampbox" role="note">
        <strong>Held for recovery</strong>
        <p>{game.queueReason || "This game did not pass the current browser launch audit."}</p>
      </div>
    </div>
  );
}
